import { useCallback, useMemo, useRef, useState } from 'react';
import { attachmentRejectionReason } from '../components/inbox/AttachmentBar';
import { errorText } from '../lib/apiErrors';

let seq = 0;
const nextId = () => `att-${++seq}-${Date.now()}`;

/**
 * Composer attachment state: add, upload, remove, and what the send path sends.
 *
 * Owned by the CALLER rather than by `MailComposer`, for the same reason the
 * inserted signature is: the SEND path needs these values, and the send path
 * lives in the caller. A second copy inside the composer would be a second
 * source of truth — and the failure mode there is a message that goes out
 * without the file the user attached.
 *
 * Each entry is `{ id, name, size, contentType, status, progress, error, s3Key }`
 * where `status` is `'uploading' | 'ready' | 'error'`. Only `ready` entries
 * reach the wire; `pendingCount` is what the caller blocks Send on, so a send
 * can never race an upload that has not finished.
 *
 * The size rules come from `attachmentRejectionReason`, shared with the bar that
 * renders them — and enforced AGAIN server-side, because a client-side limit is
 * a convenience, never the authorisation.
 *
 * @param {(file: File, onProgress: (pct:number)=>void) => Promise<{s3_key:string}>} upload
 */
export function useAttachments(upload) {
    const [attachments, setAttachments] = useState([]);
    // Entries removed while their upload was still in flight: the resolution
    // must not resurrect them.
    const abandoned = useRef(new Set());

    const patch = useCallback((id, changes) => {
        setAttachments(prev => prev.map(a => (a.id === id ? { ...a, ...changes } : a)));
    }, []);

    const add = useCallback(async (files) => {
        const accepted = [];
        const rejected = [];
        // Validate against the list AS IT GROWS, so two files that each fit but
        // together exceed the total cap are caught.
        let running = attachments.slice();
        for (const file of files) {
            const reason = attachmentRejectionReason(file, running);
            if (reason) { rejected.push(reason); continue; }
            const entry = {
                id: nextId(),
                name: file.name,
                size: file.size,
                contentType: file.type || 'application/octet-stream',
                status: 'uploading',
                progress: 0,
                error: null,
                s3Key: null,
            };
            accepted.push({ entry, file });
            running = [...running, entry];
        }

        if (accepted.length) {
            setAttachments(prev => [...prev, ...accepted.map(a => a.entry)]);
        }

        await Promise.all(accepted.map(async ({ entry, file }) => {
            try {
                const result = await upload(file, pct => {
                    if (!abandoned.current.has(entry.id)) patch(entry.id, { progress: pct });
                });
                if (abandoned.current.has(entry.id)) return;
                patch(entry.id, {
                    status: 'ready',
                    progress: 100,
                    s3Key: result?.s3_key || result?.s3Key || null,
                });
            } catch (err) {
                if (abandoned.current.has(entry.id)) return;
                // Surfaced in the composer, BEFORE send — #623 is explicit that
                // a silent failure discovered after sending is the worst case.
                // Always a STRING: a 413/415 `detail` is an object
                // ({code, message, filename, ...}) and rendering it as a React
                // child is error #31, which took the whole composer down.
                patch(entry.id, {
                    status: 'error',
                    error: errorText(err, err?.message || 'Upload failed'),
                });
            }
        }));

        return rejected;
    }, [attachments, upload, patch]);

    const remove = useCallback((id) => {
        abandoned.current.add(id);
        setAttachments(prev => prev.filter(a => a.id !== id));
    }, []);

    const reset = useCallback(() => {
        attachments.forEach(a => abandoned.current.add(a.id));
        setAttachments([]);
    }, [attachments]);

    /**
     * Seed already-uploaded files, for a draft being reopened.
     *
     * The bytes are in S3 already and the draft stored the refs, so these
     * enter as `ready` with their key — there is nothing left to upload. This
     * is the missing half of draft autosave: without it a draft's attachments
     * were stored server-side and silently dropped on reopen, which is worse
     * than never saving them, because the user has no way to tell.
     *
     * Takes the wire shape (`{s3_key, filename, content_type, size}`) so the
     * caller hands over exactly what `payload` produced.
     */
    const restore = useCallback((refs) => {
        if (!refs?.length) return;
        setAttachments(refs.map(ref => ({
            id: nextId(),
            name: ref.filename || ref.name || 'attachment',
            size: ref.size || 0,
            contentType: ref.content_type || ref.contentType || 'application/octet-stream',
            status: 'ready',
            progress: 100,
            error: null,
            s3Key: ref.s3_key || ref.s3Key || null,
        })).filter(a => a.s3Key));
    }, []);

    /**
     * Only uploaded files reach the wire, and they reach it as the backend's
     * `AttachmentRef`: `{s3_key, filename, content_type, size}`. The field is
     * `filename`, NOT `name` — Pydantic would reject the whole send.
     *
     * MEMOISED, because draft autosave now watches this. Rebuilt on every
     * render it is a new array identity every render, and the composer's
     * draft value is derived from it: the dirty effect would fire on every
     * pass and the composer would POST a draft once a second forever. A send
     * path alone never noticed, because it only reads the value on click.
     */
    const payload = useMemo(
        () => attachments
            .filter(a => a.status === 'ready' && a.s3Key)
            .map(a => ({
                s3_key: a.s3Key,
                filename: a.name,
                content_type: a.contentType,
                size: a.size,
            })),
        [attachments],
    );

    return {
        attachments,
        add,
        remove,
        reset,
        restore,
        payload,
        pendingCount: attachments.filter(a => a.status === 'uploading').length,
        failedCount: attachments.filter(a => a.status === 'error').length,
    };
}

export default useAttachments;
